// Sections by game: every section in every game, average and peak, colored by status with overflow flagged.
import moment from 'moment';
import { formatDuration } from '../../live/liveMetrics';
import { C, STATUS_RGB, hexToRgb, pageHeader, sectionLabel, table, ensureSpace, queueStatus, countStatus } from './pdfTheme';

const COUNT_SECTION_LOW = 15;
const COUNT_SECTION_HIGH = 30;

export const generateSectionsByGamePage = (pdf, { gameCards, sections, isCountMode }) => {
  const games = gameCards.filter((g) => g.hasData);
  if (!games.length || !sections.length) return;

  pdf.addPage();
  let y = pageHeader(
    pdf,
    'Sections by game',
    'Each section in each game over the time window, busiest sections first. "!" marks overflow in that section and game.'
  );

  const unit = isCountMode ? 'ppl' : 'min';
  const status = (v, overflow) => (isCountMode ? countStatus(v, COUNT_SECTION_LOW, COUNT_SECTION_HIGH) : queueStatus(v, overflow));
  const metricRows = (pick) =>
    sections
      .map((id) => {
        const cells = games.map((g) => g.sections?.[id]);
        const values = cells.filter((m) => m?.hasData).map(pick);
        return { id, cells, avg: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null };
      })
      .filter((r) => r.avg !== null)
      .sort((a, b) => b.avg - a.avg);

  const gameColumns = (pick, display, colorFor) =>
    games.map((g, gi) => ({
      header: moment(g.date).format('MMM D'),
      align: 'center',
      render: (row) => {
        const m = row.cells[gi];
        if (!m?.hasData) return { text: '-', color: C.subtle };
        const v = pick(m);
        return { text: display(v, m), color: colorFor(v, m), bold: true, flag: !isCountMode && m.overflow > 0 };
      },
    }));

  const build = (title, pick, display, colorFor) => {
    y = ensureSpace(pdf, y, 30);
    y = sectionLabel(pdf, title, y);
    const rows = metricRows(pick);
    // Game color swatches above the table columns
    y = table(
      pdf,
      y,
      [
        { header: 'Section', width: 26, render: (row) => ({ text: `Section ${row.id}`, bold: true }) },
        ...gameColumns(pick, display, colorFor),
        { header: 'Section avg', width: 28, align: 'center', render: (row) => ({ text: display(row.avg), color: C.muted }) },
      ],
      rows,
      { rowHeight: 8.5 }
    );
    y += 8;
  };

  build(
    isCountMode ? 'Average count' : 'Average queue',
    (m) => m.avg,
    (v) => `${Math.round(v)} ${unit}`,
    (v) => STATUS_RGB[status(v)]
  );
  build(
    isCountMode ? 'Peak count' : 'Peak queue',
    (m) => m.peak,
    (v, m) => `${Math.round(v)}${m?.peakOverflow ? '+' : ''} ${unit}`,
    (v, m) => STATUS_RGB[status(v, m?.peakOverflow)]
  );
  if (!isCountMode && games.some((g) => Object.values(g.sections || {}).some((m) => m.overflow > 0))) {
    build(
      'Overflow time',
      (m) => m.overflow,
      (v) => (v > 0 ? formatDuration(v) : 'None'),
      (v) => (v > 0 ? C.bad : C.subtle)
    );
  }

  // Legend of game colors
  y = ensureSpace(pdf, y, 10);
  let x = 15;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  games.forEach((g) => {
    const rgb = hexToRgb(g.color);
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(x, y - 2.4, 2.8, 2.8, 0.6, 0.6, 'F');
    pdf.setTextColor(C.muted[0], C.muted[1], C.muted[2]);
    const label = `${moment(g.date).format('ddd, MMM D')}${g.title ? ` · ${g.title}` : ''}`;
    pdf.text(label, x + 4, y);
    x += pdf.getTextWidth(label) + 12;
  });
};
